<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import FullPageModal from '@/js/core/components/parts/FullPageModal.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';
import TrayDetail from '@/js/core/components/parts/trays/TrayDetail.vue';
import CommentsList from '@/js/core/components/parts/trays/CommentsList.vue';
import RichTextEditor from '@/js/core/components/parts/editor/RichTextEditor.vue';
import CommentMediaUpload from '@/js/core/components/parts/trays/CommentMediaUpload.vue';
import PersonProfileModal from '@/js/core/components/pages/PersonProfileModal.vue';
import InfoIcon from '@/js/core/components/icons/InfoIcon.vue';
import { usePersonPanel } from '@/js/core/composables/usePersonPanel';
import { useTrayViewStore } from '@/js/core/stores/tray-view-store';
import { useTraysListStore } from '@/js/core/stores/trays-list-store';
import { useUserStore } from '@/js/core/stores/user-store';
import { useCommentPolling } from '@/js/core/composables/useCommentPolling';
import { useModalClose } from '@/js/core/composables/useModalClose';
import { useProfileVisibility } from '@/js/core/composables/useProfileVisibility';
import { useTrayView } from '@/js/core/composables/useTrayView';
import { useTrayComments } from '@/js/core/composables/useTrayComments';
import { trayUuidFromRouteSlug } from '@/js/core/utils/tray-route';

const PRIVATE_PROFILE_COMMENT_MESSAGE =
  'You are not able to leave comments because your profile visibility is set to private. ' +
  'To change this, <a href="/profile">visit your profile</a> your profile and change your profile visibility setting.';

const route = useRoute();
const trayViewStore = useTrayViewStore();
const traysListStore = useTraysListStore();
const userStore = useUserStore();

const {
  selectedProfileUuid,
  panelId: personPanelId,
  openPersonPanel,
  closePersonPanel,
} = usePersonPanel();

const traySlug = computed(() => String(route.params.traySlug || ''));
const trayUuid = computed(() => trayUuidFromRouteSlug(traySlug.value));

const {
  hasNewComments,
  newCommentCount,
  start: startPolling,
  markRefreshed,
} = useCommentPolling(trayUuid);

const { tray, comments, error, isReady, trayDetailRef, handleUpdateTray } = useTrayView({
  traySlug,
  trayUuid,
  startPolling,
});

const {
  editorRef, // Note: This is bound in the template via ref="editorRef". It is not unused.
  commentMediaRef,
  commentMedia,
  isSubmitting,
  submitError,
  handlePostComment,
  handleDeleteComment,
  handleUpdateComment,
  handleRefreshComments,
} = useTrayComments({ trayUuid, markRefreshed });

const { closeModal } = useModalClose({
  fallbackRoute: { name: 'trays' },
  cleanup: () => {
    trayViewStore.reset();
  },
});

const { isProfilePrivate, ensureProfileLoaded } = useProfileVisibility();

const isEditing = computed(() => trayDetailRef.value?.isEditing === true);

const showAdminBanner = computed(() => tray.value?.is_admin_override_view === true);

// A close mid-save would unmount the form and drop any manager operations still queued.
const requestClose = () => {
  if (trayDetailRef.value?.isSaving) return;

  return closeModal();
};

const handleTrayGone = async () => {
  traysListStore.invalidate();
  await closeModal();
};

onMounted(ensureProfileLoaded);

onUnmounted(() => {
  trayViewStore.reset();
});
</script>

<template>
  <FullPageModal v-if="isReady" :title="tray?.title || ''" @close="requestClose">
    <template v-if="showAdminBanner" #header-top>
      <p class="view-tray-modal__admin-banner">
        You are able to view this private Tray because you are an Administrator.
      </p>
    </template>

    <div v-if="error" class="view-tray-modal__error">
      {{ error }}
    </div>

    <div v-else-if="tray" class="view-tray-modal__content">
      <TrayDetail
        ref="trayDetailRef"
        :tray="tray"
        :comment-count="trayViewStore.commentsMeta?.total ?? 0"
        :active-profile-uuid="selectedProfileUuid"
        :person-panel-id="personPanelId"
        @update="handleUpdateTray"
        @deleted="handleTrayGone"
        @left="handleTrayGone"
        @person-click="openPersonPanel"
      />

      <div v-if="hasNewComments" v-show="!isEditing" class="view-tray-modal__new-comments-banner">
        <span>{{ newCommentCount }} new comment{{ newCommentCount > 1 ? 's' : '' }} available</span>
        <button type="button" class="view-tray-modal__refresh-btn" @click="handleRefreshComments">
          Refresh
        </button>
      </div>

      <div
        v-show="!isEditing"
        class="view-tray-modal__comment-section"
        :class="{
          'view-tray-modal__comment-section--message':
            userStore.isGuest || isProfilePrivate || !tray.can_comment,
        }"
      >
        <div v-if="userStore.isGuest" class="view-tray-modal__guest-message">
          <InfoIcon class="view-tray-modal__guest-message-icon" />
          <p class="view-tray-modal__guest-message-title">You're viewing as a guest</p>
          <p class="view-tray-modal__guest-message-body">
            Guest access lets you read trays, but not comment on them. Ask the person who shared
            this link if you need to join the conversation.
          </p>
        </div>
        <div v-else-if="isProfilePrivate" class="view-tray-modal__visibility-message">
          <span v-html="PRIVATE_PROFILE_COMMENT_MESSAGE"></span>
        </div>
        <template v-else-if="tray.can_comment">
          <h3 class="view-tray-modal__comment-heading">Comment</h3>
          <div class="view-tray-modal__comment-form">
            <RichTextEditor
              ref="editorRef"
              placeholder="Write a comment..."
              editor-class="view-tray-modal__editor"
            />

            <div class="view-tray-modal__media-section">
              <h3 class="view-tray-modal__comment-heading">Media</h3>
              <CommentMediaUpload ref="commentMediaRef" v-model="commentMedia" />
            </div>

            <div v-if="submitError" class="view-tray-modal__submit-error" role="alert">
              {{ submitError }}
            </div>
            <button
              type="button"
              class="view-tray-modal__post-btn"
              :disabled="isSubmitting || commentMediaRef?.isUploading"
              :title="
                commentMediaRef?.isUploading
                  ? 'Please wait for media to finish uploading'
                  : undefined
              "
              @click="handlePostComment"
            >
              {{ isSubmitting ? 'Posting...' : 'Post' }}
            </button>
          </div>
        </template>
        <div v-else class="view-tray-modal__no-permission">
          You do not have permission to comment on this tray
        </div>
      </div>

      <CommentsList
        v-show="!isEditing"
        :comments="comments"
        :has-more="trayViewStore.hasMoreComments"
        :is-loading-more="trayViewStore.isLoadingMore"
        class="view-tray-modal__comments-list"
        :active-profile-uuid="selectedProfileUuid"
        :person-panel-id="personPanelId"
        @load-more="trayViewStore.loadMoreComments(trayUuid)"
        @delete="handleDeleteComment"
        @update="handleUpdateComment"
        @person-click="openPersonPanel"
      />
    </div>
    <div class="view-tray-navbar__wrapper">
      <NavigationPanel />
    </div>

    <template #overlay>
      <PersonProfileModal
        v-if="selectedProfileUuid"
        :key="selectedProfileUuid"
        :profile-uuid="selectedProfileUuid"
        :panel-id="personPanelId"
        @close="closePersonPanel"
      />
    </template>
  </FullPageModal>
</template>
